import Drawer from '@mui/material/Drawer';
import Grid from '@mui/material/Grid';
import type { SxProps, Theme } from '@mui/system';
import { useFormContext } from 'react-hook-form';

import { type DinerFieldProps } from 'reservations/availabilities/types';
import CloseButton from 'reservations/base/components/CloseButton';
import useIsDesktop from 'reservations/base/hooks/useIsDesktop';
import { useDinerAccount } from 'reservations/dinerAccount/hooks/useDinerAccount';
import { useIsSubjectToCancellationFee } from 'reservations/feePolicy/hooks';
import { DinerInputFields } from 'reservations/form/components/DinerInputFields';
import {
  DRAWER_STATES,
  useBookingForm
} from 'reservations/form/providers/BookingFormProvider';
import { ReplaceSavedCard } from 'reservations/payments/components/ReplaceSavedCard';

interface FormDrawerProps extends DinerFieldProps {
  open: boolean;
}

export const DinerFieldsDrawer = ({
  open,
  handleFormDrawerClose
}: FormDrawerProps) => {
  const { setBeginAuthParams, isDinerAccountAuthenticated } = useDinerAccount();
  const isDesktop = useIsDesktop();
  const { isModify, drawerState } = useBookingForm();
  const formContext = useFormContext();
  const shouldAuthenticate = useIsSubjectToCancellationFee(
    formContext?.watch('partySize')
  );

  const handleFormDrawerOpen = () => {
    if (isModify && shouldAuthenticate && !isDinerAccountAuthenticated) {
      setBeginAuthParams(formContext?.watch('email'));
    }
  };

  const anchor = isDesktop ? 'right' : 'bottom';

  let basePaperStyles: SxProps<Theme> = {
    height: '100%'
  };

  if (isDesktop) {
    basePaperStyles.width = '400px';
  }

  return (
    <Drawer
      anchor={anchor}
      data-testid="mui-drawer"
      onClose={handleFormDrawerClose}
      // There's no actual onOpen prop, so we call it when the opening transition ends instead
      onTransitionEnd={handleFormDrawerOpen}
      open={open}
      PaperProps={{
        sx: basePaperStyles
      }}
    >
      <Grid
        container
        gap="32px"
        direction="column"
        sx={{ px: '32px', py: '64px', pt: '24px' }}
      >
        <Grid item sx={{ ml: 'auto', mr: '-25px' }}>
          <CloseButton onClick={handleFormDrawerClose} />
        </Grid>
        {drawerState === DRAWER_STATES.INPUT_FIELDS ? (
          <DinerInputFields handleFormDrawerClose={handleFormDrawerClose} />
        ) : (
          <ReplaceSavedCard />
        )}
      </Grid>
    </Drawer>
  );
};
